import { Button } from '@react-spectrum/s2';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Button as RACButton } from 'react-aria-components';

import { BlobImage } from '@/components/blob/BlobImage';
import { InstagramLogo } from '@/components/icons/socials-monochrome/InstagramLogo';
import { TikTokLogo } from '@/components/icons/socials-monochrome/TikTokLogo';
import { YouTubeLogo } from '@/components/icons/socials-monochrome/YouTubeLogo';
import {
	noDesignReadMessage,
	PLATFORM_LABEL,
} from '@/spaces/content/inspirationFormatting';
import { cn } from '@/utils/cn';

import {
	claimSocialPlayer,
	releaseSocialPlayer,
	useIsActivePlayer,
} from './activeSocialPlayer.ts';
import {
	getFrameStyle,
	getSocialEmbed,
	type SocialPlatform,
} from './socialEmbed.ts';

const PLATFORM_LOGO: Record<
	SocialPlatform,
	({ className }: { className?: string }) => React.ReactElement
> = {
	instagram: InstagramLogo,
	tiktok: TikTokLogo,
	youtube: YouTubeLogo,
};

type SocialPostCardProps = {
	sourceUrl: string;
	/** The stored platform, as the server wrote it. The header reads this and
	 * never the URL, so a record the parser cannot read still names its source.
	 * An unrecognized value renders no label and no logo rather than
	 * `undefined`. */
	platform: string;
	thumbnailUrl?: string;
	caption?: string;
	/** Start a carousel from this saved post. Renders the call to action next to
	 * the link out when supplied. */
	onBuildCarousel?: () => void;
	/** Whether the saved design can steer a carousel. An explicit `false` shows
	 * the reason the build action is missing. Absent means unknown, and renders
	 * nothing, so a card written before this field existed reads as it always
	 * did. */
	hasUsableDesign?: boolean;
	/** Mount the player on arrival instead of waiting for a press.
	 *
	 * The inline thread card sets this, so a shared post plays where it was
	 * shared. A thread that collects several saved posts therefore mounts a
	 * third-party frame per post, and only the last one holds the player. */
	autoPlay?: boolean;
	/** Give up the player slot.
	 *
	 * A grouped set keeps every member mounted and moves one into view at a time.
	 * It cannot release the slot itself, because the slot key is a per-mount id
	 * this component never exposes, so the card releases its own. */
	paused?: boolean;
};

const PlayGlyph = () => (
	<svg viewBox="0 0 24 24" className="size-6 translate-x-px" aria-hidden="true">
		<path d="M8 5.5v13l11-6.5z" fill="currentColor" />
	</svg>
);

const ShowGlyph = () => (
	<svg
		viewBox="0 0 24 24"
		className="size-6"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M14 5h5v5M19 5l-8 8M17 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4" />
	</svg>
);

/**
 * The platform icon plus the link out, as one row.
 *
 * Exported because the panel puts this on the SAME line as its footer button,
 * inside the shared panel footer, rather than under the post.
 */
export const SocialSourceLink = ({
	sourceUrl,
	platform,
}: {
	sourceUrl: string;
	platform: string;
}) => {
	const Logo = PLATFORM_LOGO[platform as SocialPlatform];
	return (
		<span className="flex items-center gap-2">
			{Logo && <Logo className="size-4 shrink-0 text-(--text-muted)" />}
			{sourceUrl && (
				<a
					data-testid="social-post-link"
					href={sourceUrl}
					target="_blank"
					rel="noreferrer"
					className="inline-flex min-h-6 items-center text-xs
						text-(--text-secondary) underline underline-offset-4
						hover:text-(--text-primary)"
				>
					View original post
				</a>
			)}
		</span>
	);
};

export const SocialPostCard = ({
	sourceUrl,
	platform,
	thumbnailUrl,
	caption,
	onBuildCarousel,
	hasUsableDesign,
	autoPlay = false,
	paused = false,
}: SocialPostCardProps) => {
	const cardId = useId();
	const isPlaying = useIsActivePlayer(cardId);
	const [frameLoaded, setFrameLoaded] = useState(false);
	const frameRef = useRef<HTMLDivElement>(null);
	const pressedPlayRef = useRef(false);

	const embed = getSocialEmbed(sourceUrl);
	const { root, frame } = getFrameStyle(embed);
	const label: string | undefined =
		PLATFORM_LABEL[platform as keyof typeof PLATFORM_LABEL];
	const actionLabel = embed?.isVideo
		? `Play this ${label ?? 'social'} post`
		: `Show this ${label ?? 'social'} post`;

	const handlePlay = useCallback(() => {
		pressedPlayRef.current = true;
		setFrameLoaded(false);
		claimSocialPlayer(cardId);
	}, [cardId]);

	// Claim the player on arrival when the caller asked for it. Guarded on the
	// embed so a post with no player never claims and silently blocks another
	// card from playing.
	//
	// The dependency is the URL STRING, never the embed object. `getSocialEmbed`
	// builds a new object every call, so an object dependency re-runs this on
	// every render, and two auto-playing cards then take the slot from each
	// other without end. A memo would also fix it, but React is free to drop a
	// memo cache, and no render loop should rest on that.
	const embedUrl = embed?.embedUrl;
	useEffect(() => {
		if (autoPlay && embedUrl) claimSocialPlayer(cardId);
	}, [autoPlay, embedUrl, cardId]);

	// Give up the slot when the caller pauses this card. The set moves a member
	// out of view this way, so the customer never hears a post they cannot see.
	useEffect(() => {
		if (paused) releaseSocialPlayer(cardId);
	}, [paused, cardId]);

	// Focus the WRAPPER, never the iframe. A focus call on a navigable container
	// is redirected into the child document, and a cross-origin child then
	// swallows the parent's window key handlers.
	//
	// Only a press earns the focus move. An arriving post claims the player on
	// its own, and moving focus for that would take the caret out of the
	// composer while the customer is still typing.
	useEffect(() => {
		if (isPlaying && pressedPlayRef.current) {
			frameRef.current?.focus({ preventScroll: true });
		}
	}, [isPlaying]);

	return (
		<div
			data-testid="social-post-card"
			className={cn(
				// No border and no shadow: the post itself carries the visual
				// weight, and a framed card competes with the player inside it.
				'flex w-full flex-col gap-2 p-3',
				root,
				// twMerge drops the capped max-w and the full width, so the column
				// is free to be as tall as the panel allows.
			)}
		>
			{/* Nothing to show inside the frame means no frame: the styled box
			    would otherwise draw as an empty grey rectangle above the caption. */}
			{(thumbnailUrl || embed) && (
				<div
					ref={frameRef}
					tabIndex={-1}
					data-testid="social-post-frame"
					className={cn(
						'bg-beacon-light-gray relative w-full overflow-hidden rounded-xl',
						`outline-none focus-visible:outline-2
						focus-visible:outline-offset-2`,
						'focus-visible:outline-(--text-primary)',
						frame,
					)}
				>
					<div
						className={cn(
							// Outside the panel the frame already carries the aspect, so the
							// sizer just fills it. Without a height the absolutely positioned
							// cover and iframe collapse to nothing.
							'relative w-full',
							// In the panel the HEIGHT is the constraint, so the sizer takes
							// the frame's height and derives its width from the ratio. That
							// keeps the whole embed visible instead of making it as wide as
							// the panel and then taller than the space left.
							'h-full',
						)}
					>
						{thumbnailUrl && (
							<BlobImage
								data-testid="social-post-thumbnail"
								src={thumbnailUrl}
								alt=""
								fallback={{}}
								loading="lazy"
								className="absolute inset-0 size-full object-cover"
							/>
						)}

						{isPlaying && embed && (
							<iframe
								data-testid="social-post-iframe"
								src={embed.embedUrl}
								title={actionLabel}
								allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
								referrerPolicy="strict-origin-when-cross-origin"
								loading="lazy"
								scrolling={embed.platform === 'instagram' ? 'no' : undefined}
								onLoad={() => setFrameLoaded(true)}
								className={cn(
									'absolute inset-0 size-full border-0 opacity-0',
									`[transition:opacity_var(--transition-fast)]
									motion-reduce:opacity-100`,
									frameLoaded && 'opacity-100',
								)}
							/>
						)}

						{!isPlaying && embed && (
							<RACButton
								data-testid="social-post-play"
								aria-label={actionLabel}
								onPress={handlePlay}
								className="absolute top-1/2 left-1/2 flex size-14
									-translate-x-1/2 -translate-y-1/2 cursor-pointer items-center
									justify-center rounded-full bg-white/90 text-(--text-primary)
									shadow-[var(--shadow-card)] backdrop-blur-sm outline-none
									data-[focus-visible]:outline-2
									data-[focus-visible]:outline-offset-2
									data-[focus-visible]:outline-(--text-primary)"
							>
								{embed.isVideo ? <PlayGlyph /> : <ShowGlyph />}
							</RACButton>
						)}
					</div>
				</div>
			)}

			{caption && (
				<p className="line-clamp-2 text-sm text-(--text-secondary)">
					{caption}
				</p>
			)}

			{!embed && label && platform !== 'upload' && (
				<p className="text-xs text-(--text-muted)">
					Open this post on {label}.
				</p>
			)}

			{hasUsableDesign === false && (
				// Say why the build action is gone. Hiding it silently reads as a
				// broken card, and the customer cannot learn that the design read
				// failed.
				<p
					data-testid="social-post-no-design"
					className="text-xs text-(--text-muted)"
				>
					{noDesignReadMessage(platform)}
				</p>
			)}

			{(sourceUrl || onBuildCarousel) && (
				<div className="flex items-center gap-2">
					<SocialSourceLink sourceUrl={sourceUrl} platform={platform} />
					{onBuildCarousel && (
						// Pushed right, so the row reads: where it came from, then what
						// to do with it.
						<div className="ml-auto">
							<Button
								data-testid="social-post-build-carousel"
								variant="accent"
								onPress={onBuildCarousel}
							>
								Build a carousel in this style
							</Button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
